<template>
<span>
  <v-row class="mx-auto mt-5">
    <v-col>
      <v-row class="mb-4">
        <v-col>
          <v-card class="mb-4">
            <v-card-subtitle style="font-size: 18px;">Filters</v-card-subtitle>
            <v-card-subtitle v-if="errorText" style="color: red;">{{errorText}}</v-card-subtitle>
            <div class="filter-row">
              <v-select
                  dense
                  outlined
                  hide-details
                  label="Range"
                  :items="rangeItems"
                  v-model="range"
                  @change="onRangeChange"
              ></v-select>
              <v-menu
                  v-if="range === 'custom'"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="startDate"
                      dense
                      outlined
                      hide-details
                      readonly
                      label="Start"
                      v-bind="attrs"
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="startDate" no-title @change="loadAnalytics"></v-date-picker>
              </v-menu>
              <v-menu
                  v-if="range === 'custom'"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="endDate"
                      dense
                      outlined
                      hide-details
                      readonly
                      label="End"
                      v-bind="attrs"
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="endDate" no-title @change="loadAnalytics"></v-date-picker>
              </v-menu>
              <v-select
                  dense
                  outlined
                  hide-details
                  label="Type"
                  :items="typeItems"
                  v-model="msgType"
                  @change="loadAnalytics"
              ></v-select>
              <v-select
                  dense
                  outlined
                  hide-details
                  label="Sender"
                  :items="senderItems"
                  v-model="sender"
                  @change="loadAnalytics"
              ></v-select>
            </div>
          </v-card>
          <home-analytics :report="report" :loading="loading"></home-analytics>
        </v-col>
      </v-row>

      <v-row class="mt-5" dense>
        <v-col cols="12">
          <v-card class="chart-card">
            <v-card-title>SMS Analytics</v-card-title>
            <v-divider></v-divider>
            <v-card-subtitle>Messages and new contacts over {{rangeLabel}}</v-card-subtitle>
            <div class="chart-box">
              <line-graph :height="260" :labels="lineLabels" :datasets="lineDatasets"></line-graph>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="chart-card">
            <v-card-title>Messages by date</v-card-title>
            <v-card-subtitle>Messages and new contacts over {{rangeLabel}}</v-card-subtitle>
            <div class="chart-box">
              <bar-graph :height="260" :labels="hourLabels" :datasets="hourDatasets"></bar-graph>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="6" md="6" sm="12">
          <v-card class="chart-card">
            <v-card-title>Top sender numbers</v-card-title>
            <div class="chart-box">
              <bar-graph :height="260" :labels="senderLabels" :datasets="senderDatasets"></bar-graph>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6" md="6" sm="12">
          <v-card class="chart-card">
            <v-card-title>Status mix</v-card-title>
            <div v-if="statusLabels.length" class="chart-box">
              <doughnut-graph :height="260" :labels="statusLabels" :datasets="statusDatasets"></doughnut-graph>
            </div>
            <v-card-subtitle v-else>No messages in this range.</v-card-subtitle>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="chart-card">
            <v-card-title>Messages by device</v-card-title>
            <div class="chart-box">
              <bar-graph :height="260" :labels="deviceLabels" :datasets="deviceDatasets"></bar-graph>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</span>
</template>

<script>
import DoughnutGraph from "@/views/Commons/Graphs/DoughnutGraph";
import BarGraph from "@/views/Commons/Graphs/BarGraph";
import LineGraph from "@/views/Commons/Graphs/LineGraph";
import HomeAnalytics from "@/views/Main/Home/HomeAnalytics";
import {mapActions, mapGetters} from "vuex";

const statusColors = {
  Delivered: "green",
  Failed: "red",
  Pending: "#9600bc",
  Sent: "#0072a7",
  Received: "#00a8a8",
  Processing: "#726c84",
  Cancelled: "#b0b0b0",
  Unknown: "#e0a100",
  Blacklist: "#333333",
};

export default {
  name: "Home",
  components: {HomeAnalytics, LineGraph, BarGraph, DoughnutGraph},
  data() {
    return {
      loading: false,
      analyticsRequest: 0,
      rangeError: "",
      range: "30d",
      startDate: "",
      endDate: "",
      msgType: "",
      sender: "",
      rangeItems: [
        {text: "Today", value: "today"},
        {text: "7 days", value: "7d"},
        {text: "30 days", value: "30d"},
        {text: "This month", value: "month"},
        {text: "This year", value: "year"},
        {text: "Custom", value: "custom"},
        {text: "All time", value: "all"},
      ],
      typeItems: [
        {text: "All messages", value: ""},
        {text: "Outgoing", value: "Sent"},
        {text: "Incoming", value: "Received"},
      ],
    };
  },
  mounted() {
    this.loadAnalytics();
  },
  methods: {
    ...mapActions(["fetchMetrics"]),
    formatDate(date) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return date.getFullYear() + "-" + month + "-" + day;
    },
    selectedRange() {
      const today = new Date();
      if (this.range === "today") {
        const day = this.formatDate(today);
        return {from: day, to: day};
      }
      if (this.range === "7d") {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return {from: this.formatDate(start), to: this.formatDate(today)};
      }
      if (this.range === "month") {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return {from: this.formatDate(start), to: this.formatDate(today)};
      }
      if (this.range === "year") {
        const start = new Date(today.getFullYear(), 0, 1);
        return {from: this.formatDate(start), to: this.formatDate(today)};
      }
      if (this.range === "all") {
        return {from: "all", to: this.formatDate(today)};
      }
      if (this.range === "custom") {
        return {from: this.startDate, to: this.endDate};
      }
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return {from: this.formatDate(start), to: this.formatDate(today)};
    },
    onRangeChange() {
      if (this.range === "custom" && (!this.startDate || !this.endDate)) {
        return;
      }
      this.loadAnalytics();
    },
    async loadAnalytics() {
      this.rangeError = "";
      const dates = this.selectedRange();
      if (!dates.from || !dates.to) {
        return;
      }
      if (this.range !== "all" && dates.from > dates.to) {
        this.rangeError = "End date is before the start date";
        return;
      }
      const request = ++this.analyticsRequest;
      this.loading = true;
      try {
        await this.fetchMetrics({
          from: dates.from,
          to: dates.to,
          msg_type: this.msgType || "",
          sender: this.sender || "",
        });
      } finally {
        if (request === this.analyticsRequest) {
          this.loading = false;
        }
      }
    },
    series(name, data, color) {
      return {
        label: name,
        data: data || [],
        fill: false,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 1,
      };
    },
  },
  computed: {
    ...mapGetters(["getMetrics", "getMetricsError"]),
    report() {
      return this.getMetrics || {};
    },
    timeline() {
      return this.report.timeline || {};
    },
    devices() {
      return this.report.devices || [];
    },
    errorText() {
      if (this.rangeError) {
        return this.rangeError;
      }
      const error = this.getMetricsError;
      if (!error) {
        return "";
      }
      if (typeof error === "string") {
        return error;
      }
      return error.message || "Could not load analytics";
    },
    rangeLabel() {
      const selected = this.rangeItems.find(item => item.value === this.range);
      return selected ? selected.text.toLowerCase() : "the selected range";
    },
    senderItems() {
      const seen = {};
      const items = [{text: "All senders", value: ""}];
      this.devices.forEach(device => {
        if (!device.phone || seen[device.phone]) {
          return;
        }
        seen[device.phone] = true;
        items.push({
          text: device.device ? device.phone + " (" + device.device + ")" : device.phone,
          value: device.phone,
        });
      });
      return items;
    },
    lineLabels() {
      return this.timeline.labels || [];
    },
    lineDatasets() {
      return [
        this.series("Delivered", this.timeline.delivered, "green"),
        this.series("Failed", this.timeline.failed, "red"),
        this.series("Pending", this.timeline.pending, "#9600bc"),
        this.series("Processing", this.timeline.processing, "#5c6bc0"),
        this.series("Received", this.timeline.received, "#00a8a8"),
        this.series("Sent", this.timeline.sent, "#0072a7"),
        this.series("Other", this.timeline.other, "#b0b0b0"),
        this.series("New contacts", this.timeline.contacts, "#726c84"),
      ];
    },
    senderLabels() {
      return (this.report.senders || []).map(item => item.label);
    },
    senderDatasets() {
      const totals = (this.report.senders || []).map(item => item.total);
      return [{
        label: "Messages",
        data: totals,
        backgroundColor: "#0072a7",
        borderWidth: 1,
      }];
    },
    statusLabels() {
      return (this.report.statuses || []).map(item => item.label);
    },
    statusDatasets() {
      const statuses = this.report.statuses || [];
      return [{
        data: statuses.map(item => item.total),
        backgroundColor: statuses.map(item => statusColors[item.label] || "#726c84"),
        borderWidth: 1,
      }];
    },
    hourLabels() {
      return this.lineLabels;
    },
    hourDatasets() {
      return this.lineDatasets;
    },
    deviceLabels() {
      return (this.report.device_messages || []).map(item => item.label);
    },
    deviceDatasets() {
      return [{
        label: "Messages",
        data: (this.report.device_messages || []).map(item => item.total || 0),
        backgroundColor: "#00a8a8",
        borderWidth: 1,
      }];
    },
  },
  watch: {
    getMetrics() {
      this.loading = false;
    },
    getMetricsError() {
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 12px 8px;
}
.filter-row > * {
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 240px;
}
.chart-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.chart-box {
  position: relative;
  flex: 1 0 280px;
  height: 280px;
  min-height: 280px;
  overflow: hidden;
}
.chart-box ::v-deep > div {
  position: absolute;
  top: 4px;
  right: 12px;
  bottom: 12px;
  left: 12px;
  width: auto;
  height: auto;
}
</style>
